import { useT } from "../../../../shared/i18n/i18n";
import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { BoardStatusDefinition, BoardView } from "../../../../entities/board/model/use-boards";
import type { WorkObjectFieldDefinition } from "../../../../entities/work-object-field/model/use-work-object-fields";
import { type PredicateBuilderState, VisualPredicateBuilder } from "../../../board-views/ui/visual-predicate-builder";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useTRPC } from "@/shared/api/trpc";

const emptyState: PredicateBuilderState = { expression: "", hasIncomplete: false, isEmpty: true, isReady: true, mode: "visual" };

export function StatusEntryRuleDialog({
  board,
  disabled,
  fields,
  onClear,
  onOpenChange,
  onSave,
  open,
  status,
}: {
  board: BoardView;
  disabled: boolean;
  fields: WorkObjectFieldDefinition[];
  onClear: (input: { boardId: string; targetStatusId: string }) => Promise<void>;
  onOpenChange: (open: boolean) => void;
  onSave: (input: { boardId: string; expression: string; targetStatusId: string }) => Promise<void>;
  open: boolean;
  status: BoardStatusDefinition | null;
}) {
  const t = useT();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const availableFields = useMemo(
    () => fields.filter((field) => board.availableFieldIds.includes(field.id) && field.key !== "title" && field.key !== "type"),
    [board.availableFieldIds, fields],
  );
  const [state, setState] = useState<PredicateBuilderState>(emptyState);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) setError(null);
  }, [open, status]);

  if (!status) return null;

  async function save() {
    if (!status) return;
    setIsSaving(true);
    setError(null);
    try {
      if (state.isEmpty) {
        if (status.entryRule) await onClear({ boardId: board.id, targetStatusId: status.id });
        onOpenChange(false);
        return;
      }
      const canonical = (await queryClient.fetchQuery(trpc.boards.inspectStatusRule.queryOptions({ expression: state.expression }))).canonicalExpression;
      await onSave({ boardId: board.id, expression: canonical, targetStatusId: status.id });
      onOpenChange(false);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not save status requirements.");
    } finally {
      setIsSaving(false);
    }
  }

  const willClear = Boolean(status.entryRule) && state.isEmpty;
  const saveDisabled =
    disabled ||
    isSaving ||
    (state.isEmpty && !status.entryRule) ||
    (state.mode === "visual" && (!state.isReady || state.hasIncomplete));

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] -translate-y-[calc(50%+5px)] overflow-y-auto sm:max-w-3xl" overlayClassName="bg-black/60">
        <DialogHeader>
          <DialogTitle>Requirements for “{status.name}”</DialogTitle>
          <DialogDescription>Items must satisfy these requirements before they can enter this status from any other status.</DialogDescription>
        </DialogHeader>

        <VisualPredicateBuilder
          disabled={disabled || isSaving}
          emptyHint="No conditions yet — this status has no requirements. Add one below."
          externalError={error}
          fields={availableFields}
          initialExpression={status.entryRule?.expression ?? ""}
          onStateChange={setState}
          resetSignal={`${status.id}:${open ? "open" : "closed"}`}
        />

        {error ? <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert> : null}

        <DialogFooter>
          <Button disabled={saveDisabled} onClick={() => void save()} type="button" variant={willClear ? "outline" : "default"}>
            {isSaving ? t("fieldDialog.saving") : willClear ? t("statuses.clearRequirements") : t("statuses.saveRequirements")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
